import { useState } from "react";
import { motion } from "framer-motion";
import { LAUNCH_EDITIONS, SCENES } from "@/lib/watchImages";
import WatchImage from "@/components/site/WatchImage";

export const TID_LAUNCH_SECTION = "section-launch-editions";
export const launchEditionTid = (slug) => `launch-edition-${slug}`;
export const launchFlipTid = (slug) => `launch-edition-flip-${slug}`;

export default function LaunchEditions() {
  return (
    <section
      id="launch-editions"
      data-testid={TID_LAUNCH_SECTION}
      className="relative py-28 sm:py-40 border-t border-[rgba(197,160,89,0.15)] overflow-hidden"
    >
      {/* Ambient cinematic backdrop: clubhouse architecture at dusk */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src={SCENES.displayCase}
          alt=""
          className="w-full h-full object-cover object-right opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#14201a] via-[#14201a]/70 to-[#14201a]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14201a]/85 via-transparent to-[#14201a]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3">
            <span className="gold-rule" />
            <span className="overline">The Launch Editions</span>
          </div>
          <h2 className="font-serif-display text-[#F5F2EB] font-light text-balance text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-6">
            Four chapters of a single{" "}
            <span className="italic text-[#C5A059]">instrument</span>.
          </h2>
          <p className="text-[#A8A9AD] text-base sm:text-lg leading-relaxed mt-8 max-w-xl">
            The opening run of CLEMENT COURSE I. Four metals, four dials,
            four strictly limited 1,500-piece editions. Plays-like-yardage
            bezel, Beaufort wind, stroke counter, slope shot-adjustment —
            with a sapphire caseback over a Swiss automatic movement.
          </p>
          <p className="overline mt-6">Hover or tap any watch to view the movement</p>
        </motion.div>

        <div className="mt-16 flex flex-wrap justify-center">
          {LAUNCH_EDITIONS.map((w, i) => (
            <EditionCard key={w.slug} edition={w} index={i} />
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD]">
          <span>Plays-like-yardage · Beaufort · Stroke · Slope · Swiss Automatic</span>
          <a
            href="#waitlist"
            className="text-[#C5A059] hover:text-[#F5F2EB] transition-colors"
          >
            Tell me which one you want →
          </a>
        </div>
      </div>
    </section>
  );
}

function EditionCard({ edition: w, index }) {
  const [flipped, setFlipped] = useState(false);

  return (
    <motion.figure
      data-testid={launchEditionTid(w.slug)}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, delay: index * 0.08, ease: "easeOut" }}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
      className="group relative bg-[#0C0F0A] p-6 sm:p-8 flex flex-col w-full sm:w-1/2 lg:w-1/4 border border-[rgba(197,160,89,0.15)] -mt-px -ml-px box-border"
    >
      <button
        type="button"
        data-testid={launchFlipTid(w.slug)}
        onClick={() => setFlipped((f) => !f)}
        className="relative aspect-square w-full overflow-hidden focus:outline-none"
        aria-label={`Toggle ${w.name} front and caseback view`}
        style={{ perspective: 1200 }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#161B14] via-[#0C0F0A] to-[#0C0F0A]" />
        <div
          className="relative w-full h-full transition-transform duration-700"
          style={{
            transformStyle: "preserve-3d",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          <div
            className="absolute inset-0"
            style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
          >
            <WatchImage
              src={w.front}
              alt={`CLEMENT COURSE I — ${w.name} dial`}
              fallbackTitle={w.name}
              fallbackSub={`${w.ref} · Dial`}
              loading={index < 2 ? "eager" : "lazy"}
              testId={`launch-${w.slug}-front`}
            />
          </div>
          <div
            className="absolute inset-0"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            <WatchImage
              src={w.back}
              alt={`CLEMENT COURSE I — ${w.name} caseback and movement`}
              fallbackTitle={`${w.name} · Movement`}
              fallbackSub={`${w.ref} · Caseback`}
              loading="lazy"
              testId={`launch-${w.slug}-back`}
            />
          </div>
        </div>
        <span className="absolute bottom-2 left-2 text-[9px] uppercase tracking-[0.32em] text-[#C5A059]/80 pointer-events-none">
          {flipped ? "Movement" : "Dial"}
        </span>
      </button>

      <figcaption className="mt-6">
        <div className="overline mb-3">{w.ref} · {w.model}</div>
        <h3 className="font-serif-display text-2xl sm:text-3xl text-[#F5F2EB] tracking-tight font-light leading-tight">
          {w.name}
        </h3>
        <p className="text-[#A8A9AD] text-sm leading-relaxed mt-3 min-h-[3.5rem]">
          {w.notes}
        </p>
        <div className="hairline mt-6" />
        <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-[#C5A059]">
          <span>{w.metal}</span>
          <span>1,500 pieces</span>
        </div>
      </figcaption>
    </motion.figure>
  );
}
